import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '@/lib/supabase';
import React from 'react';

const publishableKey = (window as any).ENV?.VITE_STRIPE_PUBLISHABLE_KEY || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : Promise.resolve(null);

interface PlanCheckoutButtonProps {
  planId: string;
  customerEmail: string;
  children?: React.ReactNode;
  variant?: 'primary' | 'outline';
}

export const PlanCheckoutButton: React.FC<PlanCheckoutButtonProps> = ({ planId, customerEmail, children, variant = 'primary' }) => {
  const [loading, setLoading] = React.useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      console.log('🚀 Iniciando checkout Stripe:', { planId, customerEmail });

      // Obter user_id do usuário autenticado
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || null;

      // Checkout via servidor - Em produção usa a API do Vercel, em dev usa localhost
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const serverUrl = isDev
        ? ((window as any).ENV?.VITE_STRIPE_SERVER_URL || import.meta.env.VITE_STRIPE_SERVER_URL || 'http://localhost:3001')
        : '';  // Em produção, usa a API relativa do próprio domínio

      const apiEndpoint = isDev ? `${serverUrl}/create-checkout-session` : '/api/create-checkout-session';
      const successUrl = `${window.location.origin}/dashboard?checkout=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/dashboard?checkout=cancel`;

      const supabaseUrl = (window as any).ENV?.VITE_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnon = (window as any).ENV?.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
      let priceId: string | null = null;
      if (supabaseUrl && supabaseAnon && planId) {
        try {
          const fetchPlansEndpoint = `${supabaseUrl}/rest/v1/plans?id=eq.${planId}&select=stripe_price_id`;
          const planRes = await fetch(fetchPlansEndpoint, {
            headers: { apikey: supabaseAnon, Authorization: `Bearer ${supabaseAnon}` },
          });
          const planData = await planRes.json();
          priceId = planData?.[0]?.stripe_price_id || null;
        } catch { }
      }

      let sessionId: string | null = null;
      let url: string | null = null;
      let isTrial = false;
      let redirectUrl: string | null = null;

      try {
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: planId, price_id: priceId, customer_email: customerEmail, user_id: userId, success_url: successUrl, cancel_url: cancelUrl }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          // Verificar se é erro de Trial já utilizado
          if (err.code === 'TRIAL_ALREADY_USED') {
            throw new Error('Você já utilizou o período de teste gratuito. Por favor, escolha um plano pago.');
          }
          throw new Error(err.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        let json;
        try {
          json = await response.json();
        } catch (jsonError) {
          console.error("Failed to parse JSON response:", jsonError);
          throw new Error("Invalid response from server");
        }

        // Verificar se é resposta de trial (plano gratuito) ou WhatsApp
        if (json.whatsapp === true) {
          // Redirecionar para WhatsApp
          redirectUrl = json.whatsappUrl || null;
          console.log('✅ Redirecionando para WhatsApp!');
        } else if (json.trial === true) {
          isTrial = true;
          redirectUrl = json.redirectUrl || null;
          console.log('✅ Plano trial ativado com sucesso!');
        } else {
          sessionId = json.sessionId || null;
          url = json.url || null;
        }
      } catch (serverErr) {
        console.warn('⚠️ Falha no servidor Stripe local, tentando Supabase Function...', serverErr);
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: { plan_id: planId, price_id: priceId, customer_email: customerEmail, user_id: userId, success_url: successUrl, cancel_url: cancelUrl }
        });
        if (error) {
          throw new Error(error.message || 'Erro ao criar sessão de checkout');
        }
        if ((data as any)?.whatsapp === true) {
          redirectUrl = (data as any)?.whatsappUrl || null;
        } else if ((data as any)?.trial === true) {
          isTrial = true;
          redirectUrl = (data as any)?.redirectUrl || null;
        } else {
          sessionId = (data as any)?.sessionId || null;
          url = (data as any)?.url || null;
        }
      }

      // Se for WhatsApp ou trial, redirecionar
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      // Carregar Stripe
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe não pôde ser carregado. Verifique VITE_STRIPE_PUBLISHABLE_KEY.');

      if (url) {
        window.location.href = url;
        return;
      }
      const { error } = await stripe.redirectToCheckout({ sessionId: sessionId as string });

      if (error) {
        throw error;
      }

    } catch (err) {
      console.error('❌ Erro no checkout:', err);
      alert('Erro ao processar checkout: ' + (err instanceof Error ? err.message : err));
    } finally {
      setLoading(false);
    }
  };

  const buttonClasses = variant === 'primary'
    ? "px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
    : "px-4 py-2 bg-white text-green-600 border-2 border-green-600 rounded hover:bg-green-50 disabled:opacity-50";

  return (
    <button onClick={handleCheckout} disabled={loading} className={buttonClasses}>
      {loading ? 'Processando...' : (children || 'Escolher Plano')}
    </button>
  );
}; 
